import {
  ACCESS_GROUPS,
  hasAccessGroup,
  matchesAccessGroupRule,
} from "../../access-control/models/access-groups.models";
import { FEATURE_KEYS } from "../../access-control/models/role-feature.models";
import type { AdminDashboardOverview, AdminDashboardSection } from "../models/admin-dashboard.models";
import type { AdminFeatureMonitoringResponse, CronJobLogDailyCount } from "../models/admin-monitoring.models";
import { ADMIN_MONITORING_SECTIONS, type AdminSidebarItem } from "../models/admin.models";
import { ADMIN_DASHBOARD_JOB_TREND_DAYS } from "./admin-dashboard.constants";
import { hasAdminSidebarFeatureSectionAccess } from "./admin-entry-access.constants";

// Reuse each destination's access rules before loading its summary or offering a drill-through link.
export const canViewAdminDashboardSection = (
  section: AdminDashboardSection,
  groups: readonly string[],
  hasFeature: (featureKey: string) => boolean,
): boolean => {
  if (!hasAccessGroup(groups, ACCESS_GROUPS.Admin)) {
    return false;
  }

  if (section.id === "keysSecrets") {
    return true;
  }

  if (section.id === "crewReport") {
    return hasFeature(FEATURE_KEYS.ADMIN_CREW_REPORT);
  }

  if (section.id === "cronJobs") {
    return hasFeature(FEATURE_KEYS.ADMIN_DEBUG_MONITORING);
  }

  const monitoringSection = ADMIN_MONITORING_SECTIONS.find((item) => item.id === section.id);

  return Boolean(monitoringSection && hasAdminSidebarFeatureSectionAccess(monitoringSection, groups, hasFeature));
};

export const canViewAdminDashboardManagementLink = (
  item: AdminSidebarItem,
  groups: readonly string[],
  hasFeature: (featureKey: string) => boolean,
): boolean => {
  const hasRequiredFeatures =
    (!item.requiredFeatureKey || hasFeature(item.requiredFeatureKey)) &&
    (!item.requiredFeatureKeys?.length || item.requiredFeatureKeys.some(hasFeature));

  return hasRequiredFeatures && matchesAccessGroupRule(groups, item.requiredGroups);
};

export const selectAdminDashboardMonitoringOverview = (
  response: AdminFeatureMonitoringResponse,
  section: AdminDashboardSection,
): AdminDashboardOverview => ({
  metrics: section.metricKeys.flatMap((key) => response.kpis.filter((metric) => metric.key === key)),
  breakdown: response.breakdowns.find((breakdown) => breakdown.key === section.breakdownKey),
});

// Reserve a slot for every date in the recent window, including dates for which the log API returns no rows.
// Use UTC dates so browser timezone and daylight-saving changes cannot shift the date-only log keys.
export const getAdminDashboardJobTrendDays = (dailyCounts: CronJobLogDailyCount[]): CronJobLogDailyCount[] => {
  const countsByDay = new Map(dailyCounts.map((day) => [day.day, day]));
  const today = new Date();

  return Array.from({ length: ADMIN_DASHBOARD_JOB_TREND_DAYS }, (_, index) => {
    const date = new Date(today);
    date.setUTCDate(today.getUTCDate() - ADMIN_DASHBOARD_JOB_TREND_DAYS + 1 + index);
    const day = date.toISOString().slice(0, 10);

    return countsByDay.get(day) ?? { day, total: 0, errors: 0 };
  });
};
